import { BreadCrumbOption } from '@service-victoria/component-library/components/BreadCrumb';
import { SupportedCountries } from 'interfaces/common';

export const Page = {
  title: 'COVID-19 border entry'
};

export const PageConfig = {
  LANDING_PAGE: process.env.REACT_APP_CORONAVIRUS_LANDING_PAGE,
  IS_RETURN_ENABLED: process.env.REACT_APP_CAPTURE_RETURN_DETAILS !== 'false' // if undefined, treat it as true
};

/**
 * Maximum time allowed for keeping the user ssession active wihtout any action
 */
export const SESSION_TIMEOUT_THRESHOLD_MILLISECONDS = 300000;

export const STATUS_CODES = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500
};

export const REGEX = {
  POSTCODE: /^(?:(?:[2-8]\d|9[0-7]|0?[28]|0?9(?=09))(?:\d{2}))$/,
  // Allowing "-" and "'" in the suburb because there are a few suburbs
  // that contains this special character (e.g: Mil-Lel, Mount Kuring-gai, O'Reilly, O'Connor)

  SUBURB: /^[a-zA-Z-' ]+$/
};

export const GenericValidationErrors = {
  required: 'We can’t help you without this information.',
  nameChars:
    'Sorry, names must start and end with A-Z. Punctuation in the middle is fine. e.g. O’Connor, al-Tammar.',
  genericError: 'We can’t help you without this information.',
  maxLength: (limit: number): string => `The max length for this field is ${limit} characters.`
};

export const AddressValidationErrors = {
  streetName: 'Please enter a valid street address.',
  suburb: 'Please enter a valid suburb.',
  place: 'We can’t help you without this information.',
  postcode: 'Please enter a 4-digit postcode.',
  noPostBox: "Sorry, the address can't be a PO Box, GPO Box or Locked Bag.",
  pickAddress:
    "Please choose an address from the list. If your address isn't listed, enter it manually.",
  overseasAddressCharacterLimit: "Sorry, the address can't be more than 120 characters."
};

export const DateValidationErrors = {
  day: 'That doesn’t look right. The day can only be a number between 1 and 31.',
  month: 'That doesn’t look right. The month can only be a number between 1 and 12.',
  year: 'That doesn’t look right. Try entering the year again.',
  invalidDate: 'That date doesn’t exist. Try entering the date again.'
};

export const StorageKeys = {
  State: 'ServiceVictoria.Covid19.State', // TODO: REMOVE THE `State` variable (not needed for this)
  Wizard: 'ServiceVictoria.Covid19.Wizard'
};

export const Routes = {
  Root: '/',
  Home: '/home',
  Transaction: '/Transaction',
  TechnicalError: '/TechnicalError',
  Summary: '/summary',
  Others: '*'
};

export const breadCrumbs: BreadCrumbOption[] = [
  {
    text: 'COVID-19',
    link: 'https://service.vic.gov.au/covid-19'
  },
  {
    text: 'COVID-19 border entry',
    link: ''
  }
];

/**
 * Date constants values
 */
export const Dates = {
  /**
   * Char seperator between the date different sections (day, month, year)
   */
  DATE_DIVIDER_CHAR: '/',

  /**
   * Date format for full mode
   */
  FULL_DATE_FORMAT: 'YYYY-MM-DD',

  /**
   * Date format for partial mode
   */
  MONTH_YEAR_FORMAT: 'MM/YYYY',

  /**
   * Display date format in UX
   */
  DISPLAY_FORMAT: 'DD MMMM YYYY'
};

export const DefaultErrorResponse = {
  title: 'Sorry, there was a problem processing this request.',
  value: 'Please try again.'
};

/**
 * user setttings as part of OIDC
 */
export const UserManagerSettings = {
  scope: 'openid customer:rw profile email',
  authority: process.env.REACT_APP_AUTHORITY_URL,
  client_id: 'loa'
};

export const USER_MANAGER_SESSION_KEY = `sv-tokenuser:${UserManagerSettings.authority}:${UserManagerSettings.client_id}`;

export const ExternalLinks = {
  CoronavirusVicHome: 'https://www.coronavirus.vic.gov.au',
  HomeAffairsPrepareToTravel:
    'https://covid19.homeaffairs.gov.au/preparing-to-travel-to-australia-from-overseas',
  CoronavirusVicLandingPage: 'https://www.coronavirus.vic.gov.au/victorian-border-crossing-permit',
  CoronavirusVicTravelPermitSystem:
    'https://www.coronavirus.vic.gov.au/victorian-travel-permit-system',
  CoronavirusVicTravelPermitSystemZones:
    'https://www.coronavirus.vic.gov.au/victorian-travel-permit-system#map-of-zones',
  CoronavirusRedZone: 'https://www.coronavirus.vic.gov.au/victorian-travel-permit-system#red-zone',
  CoronavirusExceptionsAndExemptions:
    'https://www.coronavirus.vic.gov.au/travel-permit-exceptions-exemptions',
  CoronavirusVicCrossBorderExtremePermit:
    'https://www.coronavirus.vic.gov.au/victorian-travel-permit-system#nswvictoria-cross-border-residents---extreme-risk-zone-permit',
  CoronavirusAuthorisedWorkerList:
    'https://www.coronavirus.vic.gov.au/authorised-provider-and-authorised-worker-list',
  FreightMovementCode:
    'https://www.infrastructure.gov.au/vehicles/vehicle_regulation/files/freight-movement-code-for-the-domestic-border-controls.pdf',
  TravellersSpecifiedWorkerPermit:
    'https://www.coronavirus.vic.gov.au/travellers-eligible-to-apply-for-specified-worker-permit',
  SpecifiedWorkerPermit: 'https://www.coronavirus.vic.gov.au/specified-worker-permit',
  TestingLocationsVictoria: 'https://www.dhhs.vic.gov.au/where-get-tested-covid-19',
  CrossBorderCommunities: 'https://www.coronavirus.vic.gov.au/information-cross-border-communities',
  CrossBorderCommunitiesAdvice:
    'https://www.coronavirus.vic.gov.au/information-cross-border-communities#advice-for-cross-border-community-members',
  CrossBorderLgaList:
    'https://www.coronavirus.vic.gov.au/information-cross-border-communities#new-south-wales',
  WhatAreExemptionsExceptions:
    'https://www.coronavirus.vic.gov.au/travel-permit-exceptions-exemptions',
  CoronavirusVicQandA: 'https://www.coronavirus.vic.gov.au/victorian-travel-permit-system-faqs',
  HotZoneDefinition: 'https://www.coronavirus.vic.gov.au/victorian-border-crossing-permit',
  GreenZoneDefinition:
    'https://www.coronavirus.vic.gov.au/victorian-border-crossing-permit#green-zone---new-south-wales-excluding-northern-beaches-and-greater-sydney-and-central-coast',
  CoronavirusVicSymptomsPage:
    'https://www.dhhs.vic.gov.au/victorian-public-coronavirus-disease-covid-19',
  RedZoneDefinition: 'https://www.coronavirus.vic.gov.au/victorian-border-crossing-permit',
  OrangeZoneDefinition:
    'https://www.coronavirus.vic.gov.au/victorian-travel-permit-system#orange-zone',
  DiagnosedPersonNCloseContact:
    'https://www.dhhs.vic.gov.au/diagnosed-persons-and-close-contacts-directions-no-14-pdf',
  DiagnosedPersonsandCloseContactsDirections:
    'https://www.dhhs.vic.gov.au/sites/default/files/documents/202012/Diagnosed%20Persons%20and%20Close%20Contacts%20Directions%20%28No%2014%29%20-%206%20December%202020%20signed.pdf',
  CommercialFreightServices:
    'https://www.infrastructure.gov.au/vehicles/vehicle_regulation/files/freight-movement-code-for-the-domestic-border-controls.pdf',
  EssentialServices:
    'https://content.legislation.vic.gov.au/sites/default/files/dce91bb1-856a-3774-ba0b-50a0fe8d7c39_58-6244aa054%20authorised.pdf',
  AgricultureServices:
    'https://www.agriculture.gov.au/sites/default/files/documents/agriculture-workers-code.pdf',
  PermittedWorker:
    'https://www.coronavirus.vic.gov.au/travellers-eligible-apply-permit-under-permitted-workers-categories',
  HotOrRedZone: 'https://www.coronavirus.vic.gov.au/domestic-travel-restrictions-entry-Victoria',
  TransitPermit: 'https://www.coronavirus.vic.gov.au/victorian-travel-permit-system#transit-permit',
  CovidDigitalCertInApp: 'https://service.vic.gov.au/covid-19/add-covid-19-digital-certificate',
  ImmunisationRegister:
    'https://www.servicesaustralia.gov.au/individuals/services/medicare/australian-immunisation-register',
  VaccinedApprovedTga: 'https://www.tga.gov.au/covid-19-vaccine-provisional-registrations',
  GetTested: 'https://www.coronavirus.vic.gov.au/where-get-tested-covid-19',
  InternationalHealthAlert:
    'https://www.health.gov.au/news/health-alerts/novel-coronavirus-2019-ncov-health-alert/coronavirus-covid-19-travel-and-restrictions/coronavirus-covid-19-advice-for-international-travellers/coronavirus-covid-19-faqs-international-travellers-to-australia',
  FederalGovInternationInfo:
    'https://www.health.gov.au/news/health-alerts/novel-coronavirus-2019-ncov-health-alert/coronavirus-covid-19-travel-and-restrictions/international-travel-and-covid-19',
  CovidMedicalExemption:
    'https://www.coronavirus.vic.gov.au/information-overseas-travellers#if-i-have-a-medical-condition-that-prevents-me-from-receiving-a-covid-19-vaccine-what-do-i-need-to-prove-this-to-enter-victoria',
  CovidHotelQuarantine: 'https://www.coronavirus.vic.gov.au/hotel-quarantine'
};

export const ListOfStates = ['VIC', 'ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'WA'].map(value => ({
  label: value,
  value
}));

export const SupportedCountriesList: SupportedCountries[] = [
  {
    label: 'Australia',
    value: 'AUS',
    isFreightWorkerEnabled: true
  },
  {
    label: 'New Zealand',
    value: 'NZL',
    isFreightWorkerEnabled: false
  }
];

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const ReturningHomeEndDate = process.env.REACT_APP_RETURN_END_DATE!;
export const ReturningHomeLgas = (process.env.REACT_APP_LGAS_FOR_RETURN || '').split(',');

export const vaxStatusFieldOptions = {
  fullyVaccinated: {
    value: 'Fully vaccinated (double-dose)',
    dataTestId: 'VaxStatusFull-RadioButton'
  },
  unvaccinatedOrPartial: {
    value: 'Not vaccinated / received first dose only / medically exempt',
    dataTestId: 'VaxStatusIncomplete-RadioButton'
  },
  internationalFullyVaccinated: {
    value: `I'm fully vaccinated or have a medical exemption`,
    dataTestId: 'VaxStatusFullInternational-RadioButton'
  },
  internationalFullyVaccinatedParentOrGuardian: {
    value: `I'm between 12 years and 2 months of age and 17 years of age and travelling alone, or with at least one fully vaccinated or medically exempt parent or guardian`,
    dataTestId: 'VaxStatusFullInternationalGuardian-RadioButton'
  },
  internationalUnder12: {
    value: `I'm under 12 years and 2 months of age and travelling alone`,
    dataTestId: 'VaxStatusFullInternationalUnder12-RadioButton'
  }
};

export const LastDayOfPermits = `2022-03-14`;
export const LastDayOfPermitsText = `14 March, 2022`;
